<template>
  <div class="smart-search">
    <div v-if="!itemSelect" class="search-box">
      <input
        v-model="searchQuery"
        class="input"
        type="search"
        :placeholder="inputPlaceholder"
        :disabled="disabled"
        autocomplete="off"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @keydown.enter.prevent="pickFirst"
        @keydown.esc.prevent="closeSuggestions"
      />

      <ul
        v-if="showSuggestions && canShowSuggestions && (suggestions.length || loading || error || hasSearched)"
        class="suggestions"
        @mousedown.prevent
      >
        <li v-if="loading" class="meta">Ищем...</li>
        <li v-else-if="error" class="meta error">{{ error }}</li>
        <li v-else-if="!suggestions.length" class="meta">
          {{ emptyHintText }}
        </li>
        <template v-else>
          <li
            v-for="item in suggestions"
            :key="item.id"
            class="suggestion-item"
            @mousedown.prevent="selectItem(item)"
          >
            <span class="primary">{{ primaryText(item) }}</span>
            <span v-if="secondaryText(item)" class="secondary">{{ secondaryText(item) }}</span>
          </li>
          <li class="meta hint" @mousedown.prevent>
            <span class="primary">{{ countHintText }}</span>
          </li>
          <li class="meta close-row" @mousedown.prevent="closeSuggestions">
            <button type="button" class="close-btn">Закрыть</button>
          </li>
        </template>
      </ul>
    </div>

    <div v-else class="selected-block">
      <ExecutorHubPreview :model="model" :item="itemSelect" />
      <ExecutorHubActions
        v-if="showActions"
        :editable="editable"
        replaceable
        @edit="editItem"
        @replace="resetItem"
      />
    </div>
  </div>
</template>

<script>
import { computed, onUnmounted, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { searchEntitySuggestions } from '@/backend/entitySmartSearchApi'
import ExecutorHubActions from '@/components/UI/ExecutorHubActions.vue'
import ExecutorHubPreview from '@/components/UI/ExecutorHubPreview.vue'

const PLACEHOLDERS = {
  companies: 'Название или ИНН',
  drivers: 'Фамилия или телефон',
  managers: 'Имя или телефон',
  cars: 'Гос. номер',
  trailers: 'Гос. номер',
}

const MIN_QUERY_LENGTH = 2
const SEARCH_DEBOUNCE_MS = 600

export default {
  name: 'EntitySmartSearch',
  components: { ExecutorHubActions, ExecutorHubPreview },
  props: {
    model: {
      type: String,
      required: true,
      validator: (value) =>
        ['companies', 'drivers', 'managers', 'cars', 'trailers'].includes(value),
    },
    itemSelect: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    companyId: {
      type: [Number, String],
      default: null,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:itemSelect', 'edit'],
  setup(props, { emit }) {
    const searchQuery = ref('')
    const suggestions = ref([])
    const showSuggestions = ref(false)
    const loading = ref(false)
    const error = ref('')
    const hasSearched = ref(false)
    let abortController = null
    let requestSeq = 0

    const inputPlaceholder = computed(
      () => props.placeholder || PLACEHOLDERS[props.model] || 'Поиск',
    )

    // Managers — единственный kind, у которого пустой query валиден (показывает
    // список менеджеров текущей компании). Остальные сущности требуют MIN_QUERY_LENGTH.
    const canShowSuggestions = computed(() => {
      const trimmed = String(searchQuery.value || '').trim()
      if (trimmed.length >= MIN_QUERY_LENGTH) return true
      if (props.model === 'managers' && trimmed.length === 0) return true
      return false
    })

    // Контекст «менеджеры конкретной компании при пустом запросе»: server-side
    // /managers/list?company_id=X. Тексты подсказок различаем.
    const isCompanyScopedManagers = computed(() =>
      props.model === 'managers'
      && props.companyId != null && props.companyId !== ''
      && String(searchQuery.value || '').trim().length === 0,
    )

    const countHintText = computed(() => {
      const count = suggestions.value.length
      if (isCompanyScopedManagers.value) {
        return `Найдено ${count} ${pluralManagers(count)} у компании`
      }
      return `Найдено ${count} существующих объектов`
    })

    const emptyHintText = computed(() => {
      if (isCompanyScopedManagers.value) {
        return 'У этой компании пока нет менеджеров'
      }
      return 'Ничего не найдено'
    })

    const pluralManagers = (n) => {
      const mod10 = n % 10
      const mod100 = n % 100
      if (mod10 === 1 && mod100 !== 11) return 'менеджер'
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'менеджера'
      return 'менеджеров'
    }

    const shortCompanyName = (name) => {
      if (!name) return ''
      return String(name).replace('Индивидуальный предприниматель', 'ИП')
    }

    const formatDriverName = (item) => {
      const parts = [item?.last_name, item?.first_name, item?.surname].filter(Boolean)
      if (parts.length) return parts.join(' ')
      return item?.first_name || item?.name || '—'
    }

    const primaryText = (item) => {
      if (props.model === 'companies') return shortCompanyName(item.name)
      if (props.model === 'drivers') return formatDriverName(item)
      if (props.model === 'managers') return item.name
      return item.number
    }

    const secondaryText = (item) => {
      if (props.model === 'companies') {
        const parts = [item.inn, item.director, item.ogrn].filter(Boolean)
        return parts.join(' · ')
      }
      if (props.model === 'drivers') {
        const parts = [item.phone, item.passport, item.license].filter(Boolean)
        return parts.join(' · ')
      }
      if (props.model === 'managers') {
        const parts = [item.phone, item.email].filter(Boolean)
        return parts.join(' · ')
      }
      const parts = []
      if (item.vin) parts.push(`VIN: ${item.vin}`)
      if (item.ownerPhone) parts.push(item.ownerPhone)
      if (item.is_trailer !== undefined && item.is_trailer !== null) {
        parts.push(item.is_trailer ? 'прицеп' : 'тягач')
      }
      return parts.join(' · ')
    }

    const cancelInFlight = () => {
      if (abortController) {
        abortController.abort()
        abortController = null
      }
    }

    const runSearch = async (query) => {
      const q = String(query || '').trim()
      // Для менеджеров пустой query валиден: показываем список менеджеров
      // текущей компании (фильтр на бэке через company_id).
      const isEmptyManagers = props.model === 'managers' && q.length === 0
      if ((!q && !isEmptyManagers) || props.disabled) {
        cancelInFlight()
        requestSeq += 1
        suggestions.value = []
        loading.value = false
        error.value = ''
        hasSearched.value = false
        return
      }

      cancelInFlight()
      const seq = ++requestSeq
      abortController = new AbortController()
      const { signal } = abortController
      loading.value = true
      error.value = ''
      hasSearched.value = false

      try {
        const result = await searchEntitySuggestions(props.model, q, { signal, companyId: props.companyId })
        if (seq !== requestSeq) return
        suggestions.value = result
        hasSearched.value = true
      } catch (err) {
        if (
          seq !== requestSeq
          || err?.code === 'ERR_CANCELED'
          || err?.name === 'CanceledError'
          || err?.name === 'AbortError'
        ) {
          return
        }
        suggestions.value = []
        error.value = 'Ошибка поиска'
        hasSearched.value = true
      } finally {
        if (seq === requestSeq) {
          loading.value = false
          abortController = null
        }
      }
    }

    const debouncedSearch = useDebounceFn(runSearch, SEARCH_DEBOUNCE_MS)

    const onInputFocus = () => {
      showSuggestions.value = true
      // Для менеджеров с пустым полем всегда перевыгружаем список текущей
      // компании — даже если предыдущий поиск вернул «ничего не найдено»
      // или компания сменилась. Server-side через /managers/list?company_id=.
      // Для остальных моделей поиск стартует только при вводе MIN_QUERY_LENGTH символов.
      const trimmed = String(searchQuery.value || '').trim()
      if (props.model === 'managers' && trimmed.length === 0 && !loading.value) {
        runSearch('')
      }
    }

    const closeSuggestions = () => {
      showSuggestions.value = false
    }

    // Расфокус инпута — закрываем дропдаун. Небольшой timeout нужен, чтобы
    // @mousedown.prevent на suggestion-item / close-btn успели отработать
    // раньше, чем blur уберёт список из DOM.
    const onInputBlur = () => {
      setTimeout(() => {
        showSuggestions.value = false
      }, 150)
    }

    watch(searchQuery, (value) => {
      showSuggestions.value = true
      debouncedSearch(value)
    })

    watch(
      () => props.disabled,
      (disabled) => {
        if (disabled) {
          searchQuery.value = ''
          suggestions.value = []
          showSuggestions.value = false
          hasSearched.value = false
          cancelInFlight()
          requestSeq += 1
          loading.value = false
        }
      },
    )

    watch(
      () => props.companyId,
      () => {
        // Смена компании меняет скоуп поиска менеджеров (server-side filter по
        // company_id для пустого query). Сбрасываем состояние, чтобы не
        // показывать «залипшие» результаты от предыдущей компании.
        if (props.model !== 'managers') return
        cancelInFlight()
        requestSeq += 1
        suggestions.value = []
        hasSearched.value = false
        loading.value = false
        error.value = ''
        // Если дропдаун открыт и поле пустое — сразу перевыгружаем список
        // менеджеров уже новой компании.
        const trimmed = String(searchQuery.value || '').trim()
        if (showSuggestions.value && trimmed.length === 0 && !props.disabled) {
          runSearch('')
        }
      },
    )

    const selectItem = (item) => {
      searchQuery.value = ''
      suggestions.value = []
      showSuggestions.value = false
      hasSearched.value = false
      emit('update:itemSelect', item)
    }

    const resetItem = () => {
      emit('update:itemSelect', null)
      searchQuery.value = ''
      suggestions.value = []
      hasSearched.value = false
    }

    const editItem = () => emit('edit', props.itemSelect)

    const pickFirst = () => {
      if (!loading.value && suggestions.value[0]) {
        selectItem(suggestions.value[0])
      }
    }

    onUnmounted(() => {
      cancelInFlight()
      requestSeq += 1
      debouncedSearch.cancel?.()
    })

    return {
      searchQuery,
      suggestions,
      showSuggestions,
      loading,
      error,
      hasSearched,
      inputPlaceholder,
      canShowSuggestions,
      countHintText,
      emptyHintText,
      shortCompanyName,
      formatDriverName,
      primaryText,
      secondaryText,
      selectItem,
      resetItem,
      editItem,
      pickFirst,
      closeSuggestions,
      onInputFocus,
      onInputBlur,
      MIN_QUERY_LENGTH,
    }
  },
}
</script>

<style scoped>
.smart-search {
  width: 100%;
  min-width: 0;
}

.search-box {
  position: relative;
  width: 100%;
}

.input {
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 14px;
  padding: 0 10px;
}

.suggestions {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 20;
  width: 100%;
  max-height: 220px;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  overflow: auto;
  background: #fff;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
}

.suggestions li {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 8px 12px;
  cursor: pointer;
}

.suggestions li:not(.meta):hover {
  background: #f3f6fb;
}

.suggestions li.meta {
  cursor: default;
  color: #667085;
}

.suggestions li.meta.error {
  color: #dc3545;
}

.suggestions li.meta.hint {
  background: #f8f9fa;
  cursor: default;
  padding: 8px 12px;
}

.suggestions li.meta.close-row {
  background: #fff;
  cursor: default;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: 4px 8px;
}

.suggestions li.meta.close-row .close-btn {
  background: transparent;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  color: #495057;
  cursor: pointer;
  font-size: 11px;
  padding: 2px 8px;
}

.suggestions li.meta.close-row .close-btn:hover {
  background: #f3f6fb;
  border-color: #6c757d;
}

.suggestion-item .primary,
.suggestions li.meta.hint .primary {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.suggestion-item .secondary,
.suggestions li.meta.hint .secondary {
  color: #6b7280;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  overflow-wrap: anywhere;
  max-width: 100%;
}

.selected-block {
  width: 100%;
}

.data-block {
  width: 100%;
  box-sizing: border-box;
  margin-top: 2px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: #fafbfc;
}

.data-label p {
  font-weight: bold;
  margin: 0 0 4px;
}

.data-label span {
  font-weight: normal;
  word-break: break-word;
}

.select-button {
  width: 100%;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background-color: #007bff;
  color: #fff;
  cursor: pointer;
}

.bg-denied {
  background-color: #dc3545;
}

.selected-actions {
  display: flex;
  gap: 8px;
}

.selected-actions .select-button {
  flex: 1;
}

.bg-edit {
  background-color: #2563eb;
}
</style>
